import { negotiateLanguages } from "@fluent/langneg";
import { FluentBundle, FluentResource } from "@fluent/bundle";
import { ReactLocalization } from "@fluent/react";
import { createDebugFtlString } from "byzantine/src/l10n/Debug";
import { customFluentFunctions } from "byzantine/src/l10n/customFluentFunctions";
import { LANG_CHANGE } from "../browserCustomEvents";
import api from "./api";

export const isValidLocale = (locale) =>
  typeof locale === "object" &&
  typeof locale.state === "string" &&
  typeof locale.latest === "string" &&
  locale.state.length > 0 &&
  locale.latest.length > 0;

const makeBundle = (localeCode, ftlString) => {
  const bundle = new FluentBundle(localeCode, { functions: customFluentFunctions });
  bundle.addResource(new FluentResource(ftlString));
  return bundle;
};

/**
 * Determines which locales need to be loaded and returns a list
 * of `FluentBundle` instances.
 *
 * Loading is resilient by tier: the institution's own `en` bundle is the
 * floor we must have to render FI-branded content, so a failure to load it
 * (or the manifest) is fatal. Every other locale — additional languages,
 * `fi` settings translations, and the Narmi product-level `en-default`
 * fallback — is loaded best-effort: a failure drops just that locale and is
 * reported back in `warnings`, rather than blocking the whole application.
 *
 * @param {String} internalName
 * @param {String} productCode three letter Narmi product code
 * @param {String} apiUrl
 * @param {Boolean} addDebugLang when true, a "debug" locale is added to bundles
 * @returns {Object} `{ error, warnings, bundles }` where `bundles` maps locale
 *   codes to `FluentBundle` instances, `warnings` lists non-fatal dropped
 *   locales, and `error` is set only on a fatal, unrecoverable failure.
 */
export const getAllBundles = async (
  internalName,
  s3ImagesBucket,
  productCode,
  apiUrl,
  addDebugLang = false
) => {
  const warnings = [];
  const bundlesMap = {};
  const result = {
    error: undefined,
    warnings,
    bundles: bundlesMap,
  };

  // We should fail fast and hard if the manifest can not be fetched.
  // This early exit prevents the `ReactLocalization` (`l10n`) property
  // from receiving any bundles, which in turn throws a warning we can track.
  // When this happens, any `Localized` components or `getString` functions
  // will use the fallback text defined in JSX source.
  let manifest = {};
  try {
    manifest = await api.fetchFtlManifest(internalName, s3ImagesBucket, apiUrl);
  } catch (error) {
    result.error = error;
    return result;
  }

  if (!Object.keys(manifest).includes(productCode)) {
    result.error = `Product "${productCode}" not found in manifest`;
    return result;
  }

  const hasSettingsLocales = Object.keys(manifest).includes("fi");

  const productLocales = Object.fromEntries(
    Object.entries(manifest[productCode]).filter(([, data]) => isValidLocale(data))
  );

  // Only create bundles for languages that have been enabled.
  // The institution's `en.ftl` should never be treated as disabled,
  // even if we make some mistake in the manifests.
  const enabledLocales = Object.entries(productLocales).reduce(
    (allowedLocales, [localeCode, localeData]) => {
      if (localeData.state === "enabled" || localeCode === "en") {
        allowedLocales[localeCode] = localeData;
      }
      return allowedLocales;
    },
    {}
  );

  // Load every enabled institution locale in parallel, tolerating partial
  // failure. A non-`en` locale that fails to load is dropped with a warning;
  // the institution's own `en` bundle is required and its failure is fatal.
  const localeCodes = Object.keys(enabledLocales);
  const localeResults = await Promise.allSettled(
    localeCodes.map((localeCode) =>
      api.fetchInstitutionFtl(
        internalName,
        s3ImagesBucket,
        enabledLocales[localeCode].latest,
        apiUrl
      )
    )
  );

  let enError;
  localeResults.forEach((res, i) => {
    const localeCode = localeCodes[i];
    if (res.status === "fulfilled") {
      bundlesMap[localeCode] = makeBundle(localeCode, res.value);
    } else if (localeCode === "en") {
      enError = res.reason;
    } else {
      warnings.push({ scope: "locale", locale: localeCode, error: res.reason });
    }
  });

  // The institution's own English is the floor: without it we cannot render
  // FI-branded content, and falling back to generic Narmi defaults is not
  // acceptable. Treat its absence as the (only) fatal locale failure. `enError`
  // is unset when `en` simply wasn't in the manifest for this product.
  if (!bundlesMap.en) {
    result.error =
      enError || `Institution "en" translations missing from manifest for "${internalName}"`;
    return result;
  }

  // In addition to the locales for this specific product, conditionally load
  // `fi` product locales for settings translations, keyed as `<langCode>-settings`.
  // These are non-critical: a failure drops just that settings locale.
  if (hasSettingsLocales) {
    const settingLocales = Object.fromEntries(
      Object.entries(manifest.fi).filter(([, data]) => isValidLocale(data))
    );
    const settingCodes = Object.keys(settingLocales);
    const settingResults = await Promise.allSettled(
      settingCodes.map((localeCode) =>
        api.fetchInstitutionFtl(
          internalName,
          s3ImagesBucket,
          settingLocales[localeCode].latest,
          apiUrl
        )
      )
    );
    settingResults.forEach((res, i) => {
      const localeCode = settingCodes[i];
      if (res.status === "fulfilled") {
        bundlesMap[`${localeCode}-settings`] = makeBundle(`${localeCode}-settings`, res.value);
      } else {
        warnings.push({ scope: "settings", locale: localeCode, error: res.reason });
      }
    });
  }

  // The Narmi product-level `en-default` is the ultimate fallback at the bottom
  // of the locale stack. It is non-critical: if it fails we still have the
  // institution's `en`, and untranslated keys fall through to JSX source text.
  let defaultFtl = "";
  try {
    defaultFtl = await api.fetchDefaultFtl(productCode, apiUrl);
    bundlesMap["en-default"] = makeBundle("en-default", defaultFtl);
  } catch (error) {
    warnings.push({ scope: "default", error });
  }

  // The debug locale is derived from the default ftl, so only add it when the
  // default loaded successfully.
  if (addDebugLang && defaultFtl) {
    bundlesMap.debug = makeBundle("debug", createDebugFtlString(defaultFtl));
  }

  return result;
};

/**
 * @param {String} selectedLocale ISO639 two letter locale code
 * @param {Object} bundlesMap FluentBundles instances by locale code
 * @param {Function} parser custom markup parser for ReactLocalization instance
 * @returns {ReactLocalization}
 */
export const getReactLocalization = (selectedLocale, bundlesMap) => {
  const availableLocales = Object.keys(bundlesMap).filter((code) => code !== "en-default");
  let requestedLocales = [];

  if (selectedLocale) {
    requestedLocales.push(selectedLocale);
  }

  if (navigator && navigator.languages) {
    requestedLocales = [...requestedLocales, navigator.languages];
  }

  // `negotiateLanguages` resolves a list of locale codes in order, based on
  // language selection and user system preferences.
  //
  // If none of the requested locale(s) are found, we fall back to the
  // institution-specific English messages.
  const negotatedLocales = negotiateLanguages(requestedLocales, availableLocales, {
    defaultLocale: "en",
  });

  //`en-default` should always be present as the final locale in the stack
  let localeStack = [...negotatedLocales, "en-default"];

  if (selectedLocale === "debug") {
    localeStack = ["debug"];
  }

  // `en-default` (and any negotiated locale) may be absent if its fetch failed;
  // drop missing bundles so the localization still builds from what loaded.
  const bundles = localeStack.map((localeCode) => bundlesMap[localeCode]).filter(Boolean);

  const l10n = new ReactLocalization(bundles);
  window.dispatchEvent(
    new CustomEvent(LANG_CHANGE, {
      detail: {
        l10n,
        bundlesMap,
      },
    })
  );

  return l10n;
};
